import { useEffect, useMemo, useState } from 'react';
import type {
  Equipo,
  EstadoTarea,
  ProyectoDetalle,
  Tarea,
  UsuarioEquipo,
} from '@matias-asset-os/contracts';
import { localeDe, t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { MiembrosProyecto } from '../componentes/MiembrosProyecto.js';
import { PanelTarea } from '../componentes/PanelTarea.js';
import { Icono } from '../componentes/Iconos.js';

interface Props {
  idioma: Idioma;
  /** Sólo Dirección gestiona el equipo, así que sólo ella edita los miembros. */
  esDireccion: boolean;
  /** Para llegar directo desde una tarea de «Tareas del dia». */
  proyectoInicial?: string | undefined;
  onVolver: () => void;
}

const ESTADOS: EstadoTarea[] = ['TODO', 'EN_CURSO', 'BLOQUEADA', 'HECHA'];

const hoy = () => new Date().toISOString().slice(0, 10);

const iniciales = (nombre: string) =>
  nombre
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');

/**
 * Una fecha de entrega como se lee de un vistazo.
 *
 * «Hoy», «Ayer» y «Mañana» en palabras, y el resto en día y mes corto. Un
 * 2026-09-09 obliga a hacer la cuenta mentalmente cada vez.
 */
function fechaCorta(iso: string, idioma: Idioma): string {
  const dia = hoy();
  if (iso === dia) return t(idioma, 'proy.hoy');
  const d = new Date(`${iso}T00:00:00Z`);
  const ayer = new Date(`${dia}T00:00:00Z`);
  const dif = Math.round((d.getTime() - ayer.getTime()) / 86_400_000);
  if (dif === -1) return t(idioma, 'proy.ayer');
  if (dif === 1) return t(idioma, 'proy.manana');
  return d.toLocaleDateString(localeDe(idioma), {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * Proyectos: equipos y proyectos a la izquierda, la lista del proyecto abierto
 * en el centro, y la tarea abierta en un panel a la derecha.
 *
 * La forma es deliberadamente la de Asana —lista agrupada por secciones, el
 * círculo de completar a la izquierda, las columnas Responsable y Fecha de
 * entrega, el panel lateral al abrir una tarea— porque el equipo lleva años
 * trabajando así y cambiar los gestos no mejora nada: sólo obliga a
 * reaprender lo que ya se sabe.
 */
export function Proyectos({ idioma, esDireccion, proyectoInicial, onVolver }: Props) {
  const [equipos, setEquipos] = useState<Equipo[] | null>(null);
  const [detalle, setDetalle] = useState<ProyectoDetalle | null>(null);
  const [equipoPersonas, setEquipoPersonas] = useState<UsuarioEquipo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [vista, setVista] = useState<'lista' | 'tablero'>('lista');
  const [tareaAbierta, setTareaAbierta] = useState<string | null>(null);
  const [plegados, setPlegados] = useState<Record<string, boolean>>({});
  const [verHechas, setVerHechas] = useState(false);
  const [busca, setBusca] = useState('');
  const [nuevoEquipo, setNuevoEquipo] = useState('');
  const [nuevoProyecto, setNuevoProyecto] = useState<Record<string, string>>({});
  const [nuevaTarea, setNuevaTarea] = useState<Record<string, string>>({});
  const [nuevaSeccion, setNuevaSeccion] = useState('');

  const fallo = (e: unknown, respaldo: string) =>
    setError(e instanceof ErrorApi ? e.message : respaldo);

  function cargarEquipos() {
    api
      .proyEquipos()
      .then(({ equipos: lista }) => setEquipos(lista))
      .catch((e: unknown) => fallo(e, t(idioma, 'proy.noSeCargaron')));
  }

  useEffect(() => {
    cargarEquipos();
    api
      .equipo()
      .then((r) =>
        setEquipoPersonas(
          r.equipo.filter((u) => u.activo && (u.rol === 'DIRECTOR' || u.rol === 'MANAGER')),
        ),
      )
      .catch(() => undefined);
  }, []);

  /*
    Si se ha entrado apuntando a un proyecto, se abre solo. Sin esto, tocar una
    tarea en «Tareas del dia» dejaba al usuario en la lista de equipos con el
    proyecto por buscar: el enlace prometia llevar a un sitio y llevaba a la
    puerta.
  */
  useEffect(() => {
    if (proyectoInicial) void abrir(proyectoInicial);
    // Solo al entrar: despues manda lo que el usuario pulse.
  }, [proyectoInicial]);

  const abrir = (id: string) => {
    setError(null);
    setTareaAbierta(null);
    return api
      .proyAbrir(id)
      .then(({ proyecto }) => setDetalle(proyecto))
      .catch((e: unknown) => fallo(e, t(idioma, 'proy.noSeAbrio')));
  };

  /** Sustituye una tarea ya guardada en la lista, sin volver a pedir el proyecto. */
  const refrescarTarea = (tarea: Tarea) =>
    setDetalle((d) =>
      d ? { ...d, tareas: d.tareas.map((x) => (x.id === tarea.id ? { ...x, ...t } : x)) } : d,
    );

  const crearTarea = (sectionId: string | null) => {
    const clave = sectionId ?? 'sin-seccion';
    const titulo = (nuevaTarea[clave] ?? '').trim();
    if (!detalle || titulo.length === 0) return;
    api
      .proyCrearTarea(detalle.id, { titulo, sectionId })
      .then(({ tarea }) => {
        setNuevaTarea((p) => ({ ...p, [clave]: '' }));
        setDetalle((d) => (d ? { ...d, tareas: [...d.tareas, tarea] } : d));
      })
      .catch((e: unknown) => fallo(e, t(idioma, 'proy.noSeCreoTarea')));
  };

  const alternarHecha = (tarea: Tarea) => {
    api
      .proyEditarTarea(tarea.id, { estado: tarea.estado === 'HECHA' ? 'TODO' : 'HECHA' })
      .then(({ tarea }) => refrescarTarea(tarea))
      .catch((e: unknown) => fallo(e, t(idioma, 'proy.noSeCambio')));
  };

  const visibles = useMemo(() => {
    if (!detalle) return [];
    const q = busca.trim().toLowerCase();
    return detalle.tareas.filter((tarea) => {
      if (!verHechas && tarea.estado === 'HECHA') return false;
      if (q && !tarea.titulo.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [detalle, busca, verHechas]);

  // --- Un proyecto abierto -------------------------------------------------
  if (detalle) {
    const dia = hoy();
    const grupos: Array<{ id: string | null; nombre: string; tareas: Tarea[] }> = [
      ...detalle.secciones.map((s) => ({
        id: s.id as string | null,
        nombre: s.nombre,
        tareas: visibles.filter((tarea) => tarea.sectionId === s.id),
      })),
    ];
    const sueltas = visibles.filter((tarea) => tarea.sectionId === null);
    // El grupo sin sección va PRIMERO y sólo si tiene algo: en Asana las tareas
    // nuevas caen ahí, y enterrarlas al final las esconde justo al crearlas.
    if (sueltas.length > 0 || detalle.secciones.length === 0) {
      grupos.unshift({ id: null, nombre: t(idioma, 'proy.sinSeccion'), tareas: sueltas });
    }

    const fila = (tarea: Tarea) => {
      const hecha = tarea.estado === 'HECHA';
      const atrasada = !hecha && tarea.vence !== null && tarea.vence < dia;
      const responsables = tarea.responsables.filter((p) => p.rol === 'RESPONSABLE');
      const colaboradores = tarea.responsables.filter((p) => p.rol === 'COLABORADOR');
      return (
        <tr key={tarea.id} className={hecha ? 'tarea-hecha' : undefined}>
          <td className="col-marca">
            <button
              type="button"
              className={`circulo ${hecha ? 'hecha' : ''}`}
              aria-pressed={hecha}
              aria-label={t(idioma, hecha ? 'proy.reabrir' : 'proy.completar', {
                titulo: tarea.titulo,
              })}
              onClick={() => alternarHecha(tarea)}
            >
              {hecha ? '✓' : ''}
            </button>
          </td>
          <td className="col-titulo">
            <button type="button" className="enlace" onClick={() => setTareaAbierta(tarea.id)}>
              {tarea.titulo}
            </button>
            <span className="senales">
              {tarea.subtareas > 0 && (
                <span title={t(idioma, 'proy.subtareas')} className="numerico">
                  ☑ {tarea.subtareasHechas}/{tarea.subtareas}
                </span>
              )}
              {tarea.comentarios > 0 && (
                <span title={t(idioma, 'proy.comentarios')} className="numerico">
                  <Icono nombre="comentario" /> {tarea.comentarios}
                </span>
              )}
              {tarea.adjuntos > 0 && (
                <span title={t(idioma, 'proy.adjuntos')} className="numerico">
                  <Icono nombre="clip" /> {tarea.adjuntos}
                </span>
              )}
              {tarea.etiquetas.map((e) => (
                <span key={e.id} className="pastilla neutra">
                  {e.nombre}
                </span>
              ))}
            </span>
          </td>
          <td className="col-persona">
            {responsables.length === 0 ? (
              <span className="suave">—</span>
            ) : (
              <span className="fila-avatares">
                {responsables.map((p) => (
                  <span key={p.userId} className="ficha-avatar" title={p.nombre}>
                    {iniciales(p.nombre)}
                  </span>
                ))}
                {responsables.length === 1 && (
                  <span className="pequeno">{responsables[0]!.nombre}</span>
                )}
              </span>
            )}
          </td>
          <td className="col-fecha">
            {tarea.vence === null ? (
              <span className="suave">—</span>
            ) : (
              <span className={atrasada ? 'fecha-atrasada' : undefined}>{fechaCorta(tarea.vence, idioma)}</span>
            )}
          </td>
          <td className="col-persona">
            {colaboradores.length === 0 ? (
              <span className="suave">—</span>
            ) : (
              <span className="fila-avatares">
                {colaboradores.map((p) => (
                  <span key={p.userId} className="ficha-avatar" title={p.nombre}>
                    {iniciales(p.nombre)}
                  </span>
                ))}
              </span>
            )}
          </td>
        </tr>
      );
    };

    return (
      <main>
        <div className="cabecera-seccion">
          <div>
            <h1>{detalle.nombre}</h1>
            <p>
              {t(idioma, visibles.length === 1 ? 'proy.unaTarea' : 'proy.variasTareas', {
                total: visibles.length,
              })}
              {detalle.descripcion ? ` · ${detalle.descripcion}` : ''}
            </p>
          </div>
          <div className="acciones">
            <button type="button" className="secundario" onClick={() => setDetalle(null)}>
              <Icono nombre="volver" /> {t(idioma, 'proy.titulo')}
            </button>
          </div>
        </div>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div className="barra-proyecto">
          <div className="pestanas-vista">
            <button
              type="button"
              className="pestana-vista"
              aria-current={vista === 'lista' ? 'page' : undefined}
              onClick={() => setVista('lista')}
            >
              {t(idioma, 'proy.lista')}
            </button>
            <button
              type="button"
              className="pestana-vista"
              aria-current={vista === 'tablero' ? 'page' : undefined}
              onClick={() => setVista('tablero')}
            >
              {t(idioma, 'proy.tablero')}
            </button>
          </div>
          <input
            type="search"
            placeholder={t(idioma, 'proy.buscarTarea')}
            aria-label={t(idioma, 'proy.buscarTarea')}
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <label className="fila-flex pequeno">
            <input
              type="checkbox"
              checked={verHechas}
              onChange={() => setVerHechas((v) => !v)}
            />
            {t(idioma, 'proy.verCompletadas')}
          </label>
        </div>

        <div className={tareaAbierta ? 'con-panel' : undefined}>
          <div className="zona-principal">
            {vista === 'lista' ? (
              <div className="tarjeta plana tabla-scroll">
                <table className="tabla tabla-tareas">
                  <thead>
                    <tr>
                      <th className="col-marca" />
                      <th>{t(idioma, 'proy.colNombre')}</th>
                      <th className="col-persona">{t(idioma, 'proy.colResponsable')}</th>
                      <th className="col-fecha">{t(idioma, 'proy.colFecha')}</th>
                      <th className="col-persona">{t(idioma, 'proy.colColaboradores')}</th>
                    </tr>
                  </thead>
                  {grupos.map((g) => {
                    const clave = g.id ?? 'sin-seccion';
                    const plegado = plegados[clave] === true;
                    return (
                      <tbody key={clave}>
                        <tr className="fila-seccion">
                          <td colSpan={5}>
                            <button
                              type="button"
                              className="enlace"
                              aria-expanded={!plegado}
                              onClick={() =>
                                setPlegados((p) => ({ ...p, [clave]: !plegado }))
                              }
                            >
                              {plegado ? '▸' : '▾'} {g.nombre}
                            </button>
                            <span className="suave pequeno numerico">{g.tareas.length}</span>
                          </td>
                        </tr>
                        {!plegado && g.tareas.map(fila)}
                        {!plegado && (
                          <tr className="fila-anadir">
                            <td className="col-marca" />
                            <td colSpan={4}>
                              <input
                                type="text"
                                placeholder={t(idioma, 'proy.anadirTarea')}
                                aria-label={t(idioma, 'proy.anadirTareaEn', {
                                  seccion: g.nombre,
                                })}
                                value={nuevaTarea[clave] ?? ''}
                                onChange={(e) =>
                                  setNuevaTarea((p) => ({ ...p, [clave]: e.target.value }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') crearTarea(g.id);
                                }}
                              />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    );
                  })}
                </table>
              </div>
            ) : (
              <div className="crm-tablero tabla-scroll">
                {ESTADOS.map((estado) => {
                  const enEstado = visibles.filter((tarea) => tarea.estado === estado);
                  return (
                    <div key={estado} className="crm-columna">
                      <header>
                        <span className="crm-etapa-nombre">
                          {t(idioma, `proy.estado${estado}`)}
                        </span>
                        <span className="crm-etapa-meta numerico">{enEstado.length}</span>
                      </header>
                      <div className="crm-tarjetas">
                        {enEstado.map((tarea) => (
                          <article key={tarea.id} className="crm-op">
                            <button
                              type="button"
                              className="enlace crm-op-nombre"
                              onClick={() => setTareaAbierta(tarea.id)}
                            >
                              {tarea.titulo}
                            </button>
                            <div className="proy-meta">
                              {tarea.responsables
                                .filter((p) => p.rol === 'RESPONSABLE')
                                .map((p) => (
                                  <span key={p.userId} className="ficha-avatar" title={p.nombre}>
                                    {iniciales(p.nombre)}
                                  </span>
                                ))}
                              {tarea.vence && (
                                <span
                                  className={
                                    tarea.estado !== 'HECHA' && tarea.vence < dia
                                      ? 'fecha-atrasada'
                                      : 'suave'
                                  }
                                >
                                  {fechaCorta(tarea.vence, idioma)}
                                </span>
                              )}
                            </div>
                            <select
                              aria-label={t(idioma, 'proy.estadoDe', { titulo: tarea.titulo })}
                              value={tarea.estado}
                              onChange={(ev) => {
                                const estadoNuevo = ev.target.value as EstadoTarea;
                                api
                                  .proyMoverTarea(tarea.id, { estado: estadoNuevo })
                                  .then(({ tarea }) => refrescarTarea(tarea))
                                  .catch((e: unknown) => fallo(e, t(idioma, 'proy.noSeMovio')));
                              }}
                            >
                              {ESTADOS.map((e) => (
                                <option key={e} value={e}>
                                  {t(idioma, `proy.estado${e}`)}
                                </option>
                              ))}
                            </select>
                          </article>
                        ))}
                        {enEstado.length === 0 && <p className="crm-vacio">—</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="fila-flex" style={{ marginTop: '1rem' }}>
              <input
                type="text"
                placeholder={t(idioma, 'proy.nuevaSeccion')}
                aria-label={t(idioma, 'proy.nuevaSeccion')}
                value={nuevaSeccion}
                onChange={(e) => setNuevaSeccion(e.target.value)}
              />
              <button
                type="button"
                className="secundario"
                disabled={nuevaSeccion.trim().length === 0}
                onClick={() => {
                  api
                    .proyCrearSeccion(detalle.id, { nombre: nuevaSeccion.trim() })
                    .then(() => {
                      setNuevaSeccion('');
                      return abrir(detalle.id);
                    })
                    .catch((e: unknown) => fallo(e, t(idioma, 'proy.noSeCreoSeccion')));
                }}
              >
                {t(idioma, 'proy.anadirSeccion')}
              </button>
            </div>

            <MiembrosProyecto idioma={idioma} proyectoId={detalle.id} puedeEditar={esDireccion} />
          </div>

          {tareaAbierta && (
            <PanelTarea
              idioma={idioma}
              tareaId={tareaAbierta}
              equipo={equipoPersonas}
              onCerrar={() => setTareaAbierta(null)}
              onCambio={refrescarTarea}
            />
          )}
        </div>
      </main>
    );
  }

  // --- Equipos y proyectos -------------------------------------------------
  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'proy.titulo')}</h1>
          <p>{t(idioma, 'proy.sub')}</p>
        </div>
        <div className="acciones">
          <button type="button" className="secundario" onClick={onVolver}>
            <Icono nombre="volver" /> {t(idioma, 'app.volver')}
          </button>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {equipos === null && !error && <p className="suave">{t(idioma, 'app.cargando')}</p>}

      {equipos !== null && equipos.length === 0 && (
        <div className="tarjeta">
          <p>{t(idioma, 'proy.sinEquipos')}</p>
        </div>
      )}

      <div className="rejilla tarjetas">
        {equipos?.map((equipo) => (
          <section key={equipo.id} className="tarjeta">
            <div className="tarjeta-cabeza">
              <h2>{equipo.nombre}</h2>
              <span className="suave pequeno numerico">{equipo.proyectos.length}</span>
            </div>
            {equipo.proyectos.length === 0 ? (
              <p className="crm-vacio">{t(idioma, 'proy.sinProyectos')}</p>
            ) : (
              <ul className="proy-lista">
                {equipo.proyectos.map((p) => (
                  <li key={p.id}>
                    <button type="button" className="enlace" onClick={() => void abrir(p.id)}>
                      {p.nombre}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="proy-alta-proyecto">
              <input
                value={nuevoProyecto[equipo.id] ?? ''}
                placeholder={t(idioma, 'proy.nuevoProyecto')}
                aria-label={t(idioma, 'proy.nuevoProyectoEn', { equipo: equipo.nombre })}
                onChange={(ev) =>
                  setNuevoProyecto((prev) => ({ ...prev, [equipo.id]: ev.target.value }))
                }
                onKeyDown={(ev) => {
                  if (ev.key !== 'Enter') return;
                  const nombre = (nuevoProyecto[equipo.id] ?? '').trim();
                  if (nombre.length < 2) return;
                  api
                    .proyCrearProyecto({ teamId: equipo.id, nombre })
                    .then(() => {
                      setNuevoProyecto((p) => ({ ...p, [equipo.id]: '' }));
                      cargarEquipos();
                    })
                    .catch((e: unknown) => fallo(e, t(idioma, 'proy.noSeCreoProyecto')));
                }}
              />
              <button
                type="button"
                className="secundario"
                onClick={() => {
                  const nombre = (nuevoProyecto[equipo.id] ?? '').trim();
                  if (nombre.length < 2) return;
                  api
                    .proyCrearProyecto({ teamId: equipo.id, nombre })
                    .then(() => {
                      setNuevoProyecto((p) => ({ ...p, [equipo.id]: '' }));
                      cargarEquipos();
                    })
                    .catch((e: unknown) => fallo(e, t(idioma, 'proy.noSeCreoProyecto')));
                }}
              >
                +
              </button>
            </div>
          </section>
        ))}
      </div>

      <div className="tarjeta crm-alta">
        <div className="campo">
          <label htmlFor="equipo-nuevo">{t(idioma, 'proy.nuevoEquipo')}</label>
          <input
            id="equipo-nuevo"
            value={nuevoEquipo}
            placeholder={t(idioma, 'proy.nombreEquipo')}
            onChange={(ev) => setNuevoEquipo(ev.target.value)}
          />
        </div>
        <button
          type="button"
          disabled={nuevoEquipo.trim().length < 2}
          onClick={() => {
            api
              .proyCrearEquipo({ nombre: nuevoEquipo.trim() })
              .then(() => {
                setNuevoEquipo('');
                cargarEquipos();
              })
              .catch((e: unknown) => fallo(e, t(idioma, 'proy.noSeCreoEquipo')));
          }}
        >
          {t(idioma, 'proy.anadir')}
        </button>
      </div>
    </main>
  );
}
